import React, { memo, useEffect, useState } from 'react';
import { useStyles } from 'uinix-ui';

import { SharedStepperProps } from '../../types/SharedStepperProps';
import { Layout } from '../Layout/Layout';
import { Progress } from '../Progress/Progress';
import { Text } from '../Text/Text';
import { Step } from './Step';
import { Step as StepType } from './types';
import { findLastCompletedIndex, getCompletedCount } from './utils';

interface Props extends SharedStepperProps {
  /** Unique name to identify the stepper. */
  name: string;
  /** Steps container id which is used for auto setting active step in 'sections' mode when scrolling. */
  containerId?: string;
  /** Enables navigation of each step via hash link */
  enableNavigation?: boolean;
  /** Enables the progress bar */
  enableProgress?: boolean;
  /** Stepper mode. Default mode is `sections` */
  mode?: 'header-nav' | 'sections';
  /** Step classname which is needed for auto setting active step in 'sections' mode when scrolling.  */
  stepClassName?: string;
}

export const Stepper = memo(
  ({
    containerId,
    currentStepId: overrideStepId,
    enableNavigation = true,
    enableProgress,
    isOrdered = false,
    mode = 'sections',
    name,
    steps,
    stepClassName,
  }: Props) => {
    const isSectionsMode = mode === 'sections';
    const [currentStepId, setCurrentStepId] = useState(overrideStepId);
    const styles = useStyles();

    useEffect(() => {
      setCurrentStepId(overrideStepId);
    }, [overrideStepId]);

    useEffect(() => {
      if (containerId) {
        const container = document.querySelector(`#${containerId}`);
        const handleScroll = () => onScroll();
        container?.addEventListener('scroll', handleScroll);
        return () => container?.removeEventListener('scroll', handleScroll);
      }
    }, [containerId]);

    const onScroll = () => {
      const container = document.querySelector(`#${containerId}`);
      const steps = document.querySelectorAll(`.${stepClassName}`);
      if (!container || !steps.length) {
        return;
      }
      if (steps.length === 1) {
        setCurrentStepId(steps[0].id);
      }
      const containerRect = container.getBoundingClientRect();
      const visibleElements: string[] = [];
      steps.forEach((el: Element) => {
        const rectangle = el.getBoundingClientRect();
        const scrollingOffset = rectangle.height / 2;
        if (
          rectangle.top + scrollingOffset >= containerRect.top &&
          rectangle.top - scrollingOffset <= containerRect.bottom
        ) {
          visibleElements.push(el.id);
        }
      });

      const secondToLastStep = steps[steps.length - 2];
      const lastStep = steps[steps.length - 1];

      setCurrentStepId((state) => {
        if (
          lastStep?.id === visibleElements[visibleElements.length - 1] &&
          visibleElements.length >= 2 &&
          secondToLastStep.getBoundingClientRect().bottom <=
            containerRect.bottom &&
          lastStep.getBoundingClientRect().bottom >= containerRect.bottom
        ) {
          return steps[steps.length - 2].id;
        } else if (
          (state === secondToLastStep?.id && visibleElements.length === 0) ||
          lastStep.getBoundingClientRect().bottom <= containerRect.bottom
        ) {
          return steps[steps.length - 1].id;
        } else if (state !== visibleElements[0]) {
          return visibleElements[0];
        }

        return state;
      });
    };

    const handleSelectStep = (step: StepType) => {
      setCurrentStepId(step.id);
      step.onSelectStep?.();
    };

    const totalCount = steps.length;
    const lastCompletedIndex = findLastCompletedIndex(steps);
    const completedCount = getCompletedCount(steps);

    const progress = enableProgress && (
      <Layout align="center" spacing={1}>
        <Progress max={totalCount} name={name} value={completedCount} />
        <Text preset="help">
          {completedCount}/{totalCount}
        </Text>
      </Layout>
    );

    return (
      <Layout
        as="nav"
        aria-label={name}
        direction={isSectionsMode ? 'column' : 'row'}
        flex="auto"
        h="100%"
        spacing={4}
      >
        {progress}
        <Layout
          as={isOrdered ? 'ol' : 'ul'}
          direction={isSectionsMode ? 'column' : 'row'}
          styles={[styles.list.unset, styles.scroll.hidden]}
          overflowY="auto"
        >
          {steps.map((step, i) => (
            <Step
              key={step.id}
              isOrdered={isOrdered}
              isCurrent={currentStepId === step.id}
              isLast={i === totalCount - 1}
              isUnstarted={i > lastCompletedIndex + 1}
              mode={mode}
              number={isOrdered ? i + 1 : undefined}
              step={step}
              onClick={() =>
                enableNavigation ? handleSelectStep(step) : undefined
              }
            />
          ))}
        </Layout>
      </Layout>
    );
  },
);
